import React, { useState, useEffect, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { addToRecentlyViewed } from 'Api/RecentlyViewed/utility';
import {
  fetchProduct,
  fetchProductPreview,
} from 'Redux/ducks/product';
import GAnalyticsRedux from 'Helpers/GAnalyticsRedux';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import NotFound from 'Components/NotFound/NotFound';
import ProductPage from 'Components/Product/ProductPage';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import useViewport from 'Hooks/useViewport';
import ProductReviewsContext from 'Components/Product/product-reviews-context';
import SelectedTierContext from 'Components/Product/Context/selected-tier-context';
import SkeletonPlaceholder from 'Components/Utility/SkeletonPlaceholder';
import SmallUspBar from 'Components/Product/Usp/SmallUspBar';
import { history } from 'Redux/store';
import VisibleVoucher from 'Components/Vouchers/VisibleVoucher';

const BundlePage = lazy(() => import(/* webpackChunkName: "Bundle" */ 'Components/Bundle/BundlePage'));
const GiftCardPage = lazy(() => import(/* webpackChunkName: "GiftCardPage" */ 'Components/Product/GiftCardPage'));
const MysteryBundlePage = lazy(() => import(/* webpackChunkName: "MysteryBundlePage" */ 'Components/MysteryBundle/MysteryBundlePage'));

const ProductContainer = ({
  match,
  adverts,
  cartItems,
  location,
  siteTheme,
  siteBrand,
  isPreview,
  voucher,
}) => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [selectedTier, selectTier] = useState(-1);
  const [inCartTier, setInCartTier] = useState(-1);
  const [urlRedirect, setUrlRedirect] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [reviews, setReviews] = useState([]);

  const { isBelowLg } = useViewport();

  const reviewContext = { reviews, setReviews }; // Allow reviews to be updated from down the tree

  const slug = match.params.slug;
  const id = match.params.id;
  const type = match.params.type;

  // Fetch product by slug or product previews by ID
  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true);
      setNotFound(false);
      setRedirect(null);

      return fetchProduct(slug)
        .then((result) => {
          if (result.notFound) {
            setTimeout(() => {
              window.prerenderReady = true;
            }, 1500);
            return setNotFound(true);
          }

          if (result.redirect) {
            return setRedirect(result.redirect);
          }
          const foundProduct = result.product;

          if (
            window.preloadedCover &&
            window.preloadedSlug === foundProduct.slug &&
            window.preloadedCover !== foundProduct.cover
          ) {
            // If there's a preloaded cover and the cover in api response is different
            // (possible due to regional products sharing same slug) override with the preloaded one
            foundProduct.cover = window.preloadedCover;
          }

          if (
            window.preloadedBackground &&
            foundProduct.header &&
            window.preloadedSlug === foundProduct.slug &&
            window.preloadedBackground !== foundProduct.header
          ) {
            foundProduct.header = window.preloadedBackground;
          }

          const urlCorrection = ProductRequirements.WrongUrlCheck(
            foundProduct.type, foundProduct.slug, type, slug,
          );

          if (urlCorrection && urlCorrection.isWrongType && !isPreview) {
            setIsLoading(false);
            return setUrlRedirect(urlCorrection.modifiedUrl);
          }

          setProduct(foundProduct);
          setIsLoading(false);

          GAnalyticsRedux(foundProduct);
          addToRecentlyViewed(foundProduct);

          setTimeout(() => {
            window.prerenderReady = true;
          }, 1500);
          return null;
        }).catch(setFetchError);
    };

    const loadProductPreview = async () => {
      let encodedKey = '';
      const key = getUrlParameter('key');

      if (key) {
        encodedKey = encodeURIComponent(key);
      }

      const result = await fetchProductPreview(id, encodedKey);

      if (result.notFound) {
        return setNotFound(true);
      }

      return setProduct(result.product);
    };

    if (slug) {
      loadProduct();
    } else if (id) {
      loadProductPreview();
    } else {
      setNotFound(true);
    }
  }, [slug, id, isPreview, type]);

  useEffect(() => {
    if (product && product.bundles && product.bundles.length > 1) {
      const tierToSelect = (product.bundles.length - 1);

      if (product && product._id && cartItems.length) {
        const itemInCart = cartItems.find(i => i.pid === product._id);
        if (itemInCart && typeof itemInCart.tierIndex === 'number' && selectedTier !== itemInCart.tierIndex) {
          selectTier(itemInCart.tierIndex);
          setInCartTier(itemInCart.tierIndex);
        } else if (selectedTier < 0) {
          selectTier(tierToSelect);
        }
      } else if (selectedTier < 0 || selectedTier > tierToSelect) {
        selectTier(tierToSelect);
      }
    }
  }, [cartItems, product]); // eslint-disable-line react-hooks/exhaustive-deps

  if (fetchError) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    throw fetchError;
  }

  if (urlRedirect && `/${urlRedirect}` !== match.url) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    return <NotFound redirect={urlRedirect} />;
  }

  if (redirect) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    return <NotFound redirect={redirect} />;
  }

  if (notFound) return <NotFound />;

  if (isLoading) {
    return <SkeletonPlaceholder />;
  }

  if (!product) return false;

  // Shouldn't need to pass this around but can sort that out later
  const push = history.push;

  const {
    drm: platforms,
  } = product;

  const validDrm = platforms ? Object.keys(platforms).filter(d => platforms[d]) : [];

  let showUspBar = false;
  if (
    window.locale === 'en' &&
    siteBrand === 'fanatical' &&
    (validDrm[0] !== 'origin' && validDrm[0]) &&
    (['game', 'dlc', 'bundle'].includes(type))
  ) {
    showUspBar = true;
  }

  let componentToLoad = 'product';

  if (product.type === 'bundle' && product.template_type && product.template_type !== 'original') {
    componentToLoad = 'bundle';
  }

  if (product.type === 'bundle' && product.mystery) {
    componentToLoad = 'mystery';
  }

  if (product.display_type === 'gift-card') {
    componentToLoad = 'gift-card';
  }

  /* eslint-disable consistent-return */
  let comicCheck = false;
  if (product) {
    if (product.bundles) {
      product.bundles.forEach((tier) => {
        tier.games.forEach((game) => {
          if (game.type === 'comic' || game.type === 'book') {
            comicCheck = true;
          }
        });
      });
    }
  }

  const fromLocation = get(location, 'state.from', '');
  const showGiftBanner = fromLocation.includes('gift-guide');

  return (
    <SelectedTierContext.Provider value={{ selectedTier, inCartTier }}>
      {showUspBar &&
        <React.Fragment>
          <div>
            <SmallUspBar
              mobileEnabled={isBelowLg}
              drm={validDrm}
              type={product.display_type}
              comic={comicCheck}
            />
          </div>
        </React.Fragment>
      }

      <VisibleVoucher product={product} />

      {(componentToLoad === 'product') &&
        <ProductReviewsContext.Provider value={reviewContext}>
          <ProductPage
            isPreview={isPreview}
            product={product}
            adverts={adverts}
            selectTier={selectTier}
            showGiftBanner={showGiftBanner}
            siteTheme={siteTheme}
            siteBrand={siteBrand}
            voucher={voucher}
          />
        </ProductReviewsContext.Provider>
      }

      {componentToLoad === 'bundle' &&
        <Suspense>
          <BundlePage
            product={product}
            adverts={adverts}
            push={push}
            selectTier={selectTier}
            showGiftBanner={showGiftBanner}
          />
        </Suspense>
      }

      {componentToLoad === 'mystery' &&
        <Suspense>
          <MysteryBundlePage
            product={product}
            adverts={adverts}
            push={push}
            selectTier={selectTier}
          />
        </Suspense>
      }

      {componentToLoad === 'gift-card' &&
        <Suspense>
          <GiftCardPage
            product={product}
          />
        </Suspense>
      }
    </SelectedTierContext.Provider>
  );
};

const mapStateToProps = ({
  adverts: { Product: adverts },
  cart: { items: cartItems },
  router: { location },
  siteTheme,
  siteBrand,
  voucher: { voucher },
}) => ({
  adverts,
  cartItems,
  location,
  siteTheme,
  siteBrand,
  voucher,
});

export default connect(mapStateToProps)(ProductContainer);

ProductContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string,
      id: PropTypes.string,
    }),
  }).isRequired,
  adverts: PropTypes.arrayOf(PropTypes.shape({})),
  cartItems: PropTypes.arrayOf(PropTypes.shape({})),
  location: PropTypes.shape({}),
  siteTheme: PropTypes.string,
  siteBrand: PropTypes.string,
  voucher: PropTypes.shape({}),
};

ProductContainer.defaultProps = {
  product: null,
  adverts: [],
  cartItems: [],
  ageRatingBoard: 'PEGI',
  currencyCode: 'GBP',
  countryName: 'United Kingdom',
  countryCode: 'GB',
  selectedTier: -1,
  location: {},
  siteTheme: '',
  siteBrand: 'fanatical',
  voucher: {},
};
